import { useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useSiteData } from '@/context/SiteDataContext';
import PhotoUpload from '@/components/admin/PhotoUpload';
import Modal from './Modal';

export const heroImgStyle = (zoom = 1, x = 50, y = 50) => ({
  objectPosition: `${x}% ${y}%`, transform: `scale(${zoom})`, transformOrigin: `${x}% ${y}%`,
});

const KEYS = ['hero_bg_image', 'hero_bg_zoom', 'hero_bg_x', 'hero_bg_y', 'hero_bg_color', 'hero_overlay_color', 'hero_overlay_opacity', 'hero_height'];

export default function HeroEditorDialog({ slug, content, defaultImage, onClose }) {
  const { loadPageContent } = useSiteData();
  const [f, setF] = useState(() => {
    const o = {};
    KEYS.forEach(k => { o[k] = content?.[k]; });
    if (o.hero_bg_image === undefined) o.hero_bg_image = defaultImage || '';
    return o;
  });
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const zoom = f.hero_bg_zoom ?? 1, x = f.hero_bg_x ?? 50, y = f.hero_bg_y ?? 50;
  const overlayOp = f.hero_overlay_opacity ?? 0.8;

  const onDown = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, x, y, w: e.currentTarget.offsetWidth, h: e.currentTarget.offsetHeight }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const clamp = (v) => Math.max(0, Math.min(100, v));
    set('hero_bg_x', clamp(d.x - ((e.clientX - d.sx) / d.w) * 100 / zoom));
    set('hero_bg_y', clamp(d.y - ((e.clientY - d.sy) / d.h) * 100 / zoom));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/site/page-content/${slug}`, f);
      await loadPageContent(slug);
      toast.success('Header saved');
      onClose();
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  return (
    <Modal title="Edit Page Header" onClose={onClose} testid="hero-editor" wide
      footer={<>
        <button onClick={onClose} className="px-4 py-2 text-charcoal-muted">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="hero-editor-save" className="btn-royal">{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="space-y-5">
        <div className="text-xs text-charcoal-muted">Drag the preview to reposition. Use the zoom slider to crop in.</div>
        <div data-testid="hero-editor-preview" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { drag.current = null; }}
          className="relative aspect-[16/5] rounded-lg overflow-hidden cursor-move select-none touch-none"
          style={{ backgroundColor: f.hero_bg_color || 'var(--royal, #3B1E5E)' }}>
          {f.hero_bg_image && <img src={f.hero_bg_image} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={heroImgStyle(zoom, x, y)} />}
          {f.hero_bg_image && <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: f.hero_overlay_color || '#3B1E5E', opacity: overlayOp }} />}
          <div className="absolute inset-0 flex items-center justify-center text-white font-serif text-2xl font-bold pointer-events-none">Page Title</div>
        </div>
        <PhotoUpload label="Background image" value={f.hero_bg_image} onChange={(v) => set('hero_bg_image', v)} category="headers" />
        {f.hero_bg_image && <button onClick={() => set('hero_bg_image', '')} data-testid="hero-remove-image" className="text-xs text-red-600 underline">Remove image (use solid color)</button>}
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <label>Zoom ({zoom.toFixed(2)}x)
            <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => set('hero_bg_zoom', parseFloat(e.target.value))} data-testid="hero-zoom" className="w-full" /></label>
          <label>Overlay strength ({Math.round(overlayOp * 100)}%)
            <input type="range" min="0" max="1" step="0.05" value={overlayOp} onChange={(e) => set('hero_overlay_opacity', parseFloat(e.target.value))} data-testid="hero-overlay" className="w-full" /></label>
          <label>Horizontal position ({Math.round(x)}%)
            <input type="range" min="0" max="100" value={x} onChange={(e) => set('hero_bg_x', parseFloat(e.target.value))} className="w-full" /></label>
          <label>Vertical position ({Math.round(y)}%)
            <input type="range" min="0" max="100" value={y} onChange={(e) => set('hero_bg_y', parseFloat(e.target.value))} className="w-full" /></label>
          <label className="flex items-center gap-2">Background color
            <input type="color" value={f.hero_bg_color || '#3B1E5E'} onChange={(e) => set('hero_bg_color', e.target.value)} data-testid="hero-bg-color" /></label>
          <label className="flex items-center gap-2">Overlay color
            <input type="color" value={f.hero_overlay_color || '#3B1E5E'} onChange={(e) => set('hero_overlay_color', e.target.value)} /></label>
          <label>Header height
            <select value={f.hero_height || 'md'} onChange={(e) => set('hero_height', e.target.value)} data-testid="hero-height" className="w-full border rounded px-2 py-1.5 mt-1">
              <option value="sm">Compact</option><option value="md">Standard</option><option value="lg">Tall</option>
            </select></label>
        </div>
      </div>
    </Modal>
  );
}
